// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/whatsapp/hooks/useWhatsApp.ts
// ════════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappService } from '../services/whatsappService';
import type {
  EnviarWhatsAppRequest,
  SalvarWhatsAppTemplateRequest,
  SalvarConfigWhatsAppRequest,
} from '../types';

const KEYS = {
  config:    ['whatsapp', 'config'] as const,
  templates: ['whatsapp', 'templates'] as const,
  historico: (pessoaId?: number) => ['whatsapp', 'historico', pessoaId] as const,
};

// ── Config ───────────────────────────────────────────────────────────────

export function useWhatsAppConfig() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.config,
    queryFn:  whatsappService.getConfig,
  });

  const salvar = useMutation({
    mutationFn: (req: SalvarConfigWhatsAppRequest) => whatsappService.salvarConfig(req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.config }),
  });

  const testarConexao = useMutation({
    mutationFn: () => whatsappService.testarConexao(),
  });

  return { query, salvar, testarConexao };
}

// ── Templates ────────────────────────────────────────────────────────────

export function useWhatsAppTemplates() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.templates,
    queryFn:  whatsappService.listarTemplates,
  });

  const criar = useMutation({
    mutationFn: (req: SalvarWhatsAppTemplateRequest) => whatsappService.criarTemplate(req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.templates }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarWhatsAppTemplateRequest }) =>
      whatsappService.atualizarTemplate(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.templates }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => whatsappService.excluirTemplate(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.templates }),
  });

  return { query, criar, atualizar, excluir };
}

// ── Envio ────────────────────────────────────────────────────────────────

export function useEnviarWhatsApp() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (req: EnviarWhatsAppRequest) => whatsappService.enviar(req),
    onSuccess:  (_, vars) => {
      if (vars.pessoaId) qc.invalidateQueries({ queryKey: KEYS.historico(vars.pessoaId) });
    },
  });
}

export function useHistoricoWhatsApp(pessoaId?: number) {
  return useQuery({
    queryKey: KEYS.historico(pessoaId),
    queryFn:  () => whatsappService.historico({ pessoaId }),
    enabled:  pessoaId !== undefined,
  });
}
